import { useNavigate, useSearchParams } from "react-router-dom";
import { FormEvent, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import {
  Button,
  CardActions,
  CardContent,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

import LOGO from "../../semiauth.logo.png";
import format from "../utils/Role";
import {
  useVaultInviteAccept,
  useVaultInviteDetails,
} from "../../hooks/vault/base";
import TermsAndConditions from "../utils/TermsAndConditions";

const bull = (
  <Box
    component="span"
    sx={{ display: "inline-block", mx: "2px", transform: "scale(0.8)" }}
  >
    •
  </Box>
);

const AcceptInvite = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [code, setCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const handleAgreed = (e) => setAgreed(e.target.checked);
  const toggleShowCode = () => setShowCode(!showCode);

  const accept = useVaultInviteAccept();
  const details = useVaultInviteDetails(token);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // attempt invite accept
    accept.mutate({ token, code });
  };

  useEffect(() => {
    if (accept.isSuccess) navigate("/welcome");
  }, [accept.isSuccess]);

  return (
    <Container
      maxWidth="sm"
      component="main"
      sx={{
        display: "flex",
        alignItems: "center",
        marginBlock: "auto",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <img src={LOGO} alt="[semiauth] logo" className="logo" />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <CardContent>
            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Organization
            </Typography>

            <Typography variant="h5" component="div">
              {/* be{bull}nev{bull}o{bull}lent */}
              {details.data?.organization.name}
            </Typography>

            <Typography sx={{ color: "text.secondary", mb: 1.5 }}>
              {details.data?.organization.description}
            </Typography>

            <Typography
              gutterBottom
              sx={{ color: "text.secondary", fontSize: 14 }}
            >
              Role
            </Typography>

            <Typography variant="body2">
              {format(details.data?.organization.role || "").toUpperCase()}
            </Typography>
          </CardContent>

          <TextField
            required
            fullWidth
            id="invite-code"
            name="invite-code"
            label="Invite code"
            margin="normal"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            type={showCode ? "text" : "password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="toggle code visibility"
                    onClick={toggleShowCode}
                    edge="end"
                  >
                    {showCode ? (
                      <VisibilityOffOutlinedIcon />
                    ) : (
                      <VisibilityOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <CardActions>
            <Grid container>
              <Grid item xs={12} textAlign="left">
                <TermsAndConditions
                  type="platform"
                  agreed={agreed}
                  handleAgreed={handleAgreed}
                />
              </Grid>
            </Grid>
          </CardActions>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={!agreed || !code}
            sx={{ my: 2 }}
          >
            Accept Invite
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AcceptInvite;
